'use client';

import { createFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SingleCombobox,
  Textarea,
} from '@commenda/kakashi';
import { FormsTemplateStatus, FormsTemplateType } from '@commenda/types';
import { Save, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { FormBuilderHook } from '../../hooks/useFormBuilder';
import type { FormStep } from '@commenda/types';
import { getAllFieldIds } from '../../utils/dsl-traversal';
import { VisibilityEditor } from './VisibilityEditor';

export function StepEditor({
  step,
  formBuilder,
}: {
  step: FormStep;
  formBuilder: FormBuilderHook;
}) {
  const [title, setTitle] = useState(step.title);
  const [minutes, setMinutes] = useState(
    step.estimatedMinutes?.toString() || ''
  );
  const { setNotification } = useNotification();
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFormData, setSaveFormData] = useState({
    name: step.title || 'Untitled Step',
    description: '',
    status: 'DRAFT' as FormsTemplateStatus,
    version: '1.0.0',
    versionNote: '',
    key: null,
  });

  useEffect(() => {
    setTitle(step.title);
    setMinutes(step.estimatedMinutes?.toString() || '');
  }, [step]);

  const handleUpdate = (updates: Partial<FormStep>) => {
    formBuilder.updateStep(step.id, updates);
  };

  const handleSaveAsStep = async () => {
    if (!saveFormData.name) {
      setNotification({
        title: 'Error',
        message: 'Please enter a step name',
        valid: false,
        show: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const result = await createFormsTemplate({
        name: saveFormData.name,
        description: saveFormData.description || undefined,
        type: FormsTemplateType.FORM_STEP,
        status: saveFormData.status,
        dsl: step as unknown as Record<string, unknown>,
        version: saveFormData.version,
        versionNote: saveFormData.versionNote || undefined,
        key: saveFormData.key,
      });

      if (result.success) {
        setShowSaveDialog(false);
        setNotification({
          title: 'Success',
          message: 'Step saved successfully!',
          valid: true,
          show: true,
        });
      } else {
        setNotification({
          title: 'Error',
          message: 'Failed to save step. Please try again.',
          valid: false,
          show: true,
        });
      }
    } catch (error) {
      console.error('Error saving step:', error);
      setNotification({
        title: 'Error',
        message: 'Failed to save step. Please try again.',
        valid: false,
        show: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-bg-primary border-l border-border-secondary overflow-hidden">
      <div className="p-2.5 px-4 border-b border-border-secondary flex items-center justify-between">
        <div>
          <h2 className="txt-medium-medium text-text-primary">Step Settings</h2>
        </div>
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          onClick={() => formBuilder.clearSelection()}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Step Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => handleUpdate({ title })}
            />
          </div>

          <div className="space-y-2">
            <Label>Estimated Minutes</Label>
            <Input
              type="number"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              onBlur={() =>
                handleUpdate({
                  estimatedMinutes: parseInt(minutes, 10) || undefined,
                })
              }
            />
          </div>

          <VisibilityEditor
            visibility={step.visibility}
            availableFields={getAllFieldIds(formBuilder.dsl).filter(
              (f) => f.fieldId
            )}
            onChange={(visibility) => handleUpdate({ visibility })}
          />
        </div>

        {/* Save as Step Button */}
        <div className="pt-4 border-t border-border-secondary">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setSaveFormData((prev) => ({
                ...prev,
                name: step.title || 'Untitled Step',
              }));
              setShowSaveDialog(true);
            }}
            className="w-full"
          >
            <Save className="w-4 h-4 mr-2" />
            Save as Reusable Step
          </Button>
        </div>
      </div>

      {/* Save Step Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save as Reusable Step</DialogTitle>
            <DialogDescription>
              Save this step to reuse it in other forms.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 px-6">
            <div className="space-y-2">
              <Label htmlFor="stepName">Step Name *</Label>
              <Input
                id="stepName"
                value={saveFormData.name}
                onChange={(e) =>
                  setSaveFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Enter step name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stepDescription">Description</Label>
              <Textarea
                id="stepDescription"
                value={saveFormData.description}
                onChange={(e) =>
                  setSaveFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Enter step description"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="stepStatus">Status</Label>
                <SingleCombobox
                  id="stepStatus"
                  options={[
                    { label: 'Draft', value: FormsTemplateStatus.DRAFT },
                    { label: 'Live', value: FormsTemplateStatus.LIVE },
                    {
                      label: 'Completed',
                      value: FormsTemplateStatus.COMPLETED,
                    },
                  ]}
                  value={saveFormData.status}
                  setValue={(val) =>
                    setSaveFormData((prev) => ({
                      ...prev,
                      status: val as FormsTemplateStatus,
                    }))
                  }
                  placeholder="Select status"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="stepVersion">Version</Label>
                <Input
                  id="stepVersion"
                  value={saveFormData.version}
                  onChange={(e) =>
                    setSaveFormData((prev) => ({
                      ...prev,
                      version: e.target.value,
                    }))
                  }
                  placeholder="1.0.0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stepVersionNote">Version Note</Label>
              <Textarea
                id="stepVersionNote"
                value={saveFormData.versionNote}
                onChange={(e) =>
                  setSaveFormData((prev) => ({
                    ...prev,
                    versionNote: e.target.value,
                  }))
                }
                placeholder="What's included in this step?"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveAsStep}
              disabled={isSaving || !saveFormData.name}
            >
              {isSaving ? 'Saving...' : 'Save Step'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
